import invariant from 'tiny-invariant'
import {useQuery, useQueryClient} from '@github-ui/react-query'
import {reactFetchJSON} from '@github-ui/verified-fetch'
import {useCallback} from 'react'

export interface FeaturePreviewObject {
  description_html: string
  enrolled: boolean
  name: string
  slug: string
  feedback_link?: string | null
  documentation_link?: string | null
  image_link?: string | null
  toggle_feature_path: string | null
}

export interface FeaturePreviewResponse {
  features: FeaturePreviewObject[]
}

export interface FeaturePreviewQueryConfig {
  login: string
}

function updateQueryData(
  oldData: FeaturePreviewResponse | undefined,
  feature: Partial<FeaturePreviewObject> & {slug: string},
): FeaturePreviewResponse | undefined {
  if (!oldData) return oldData

  return {
    ...oldData,
    features: oldData.features.map(f => {
      if (f.slug === feature.slug) {
        return {
          ...f,
          ...feature,
        }
      }
      return f
    }),
  }
}

export function useFeaturePreviewItemsQuery(config: FeaturePreviewQueryConfig) {
  invariant(typeof config.login === 'string', 'login must be a string')

  const queryAPI = `/users/${config.login}/feature_previews`
  const queryClient = useQueryClient()

  const featureQuery = useQuery<FeaturePreviewResponse>({
    queryKey: ['feature-preview', queryAPI],
    queryFn: async () => {
      const response = await reactFetchJSON(queryAPI)
      return (await response.json()) as FeaturePreviewResponse
    },
  })

  const toggleEnrollment = useCallback(
    async (feature: FeaturePreviewObject, onShowBanner: (type: 'error' | 'reload') => void) => {
      invariant(feature.slug, 'feature.slug is required to toggle a feature preview')

      const endpoint = feature.toggle_feature_path
      invariant(endpoint, 'endpoint is required to toggle a feature preview')

      const queryKey = ['feature-preview', queryAPI]

      try {
        const responsePromise = reactFetchJSON(endpoint, {method: 'POST'})

        // Proactively update the cache to reflect the new state
        queryClient.setQueryData<FeaturePreviewResponse>(queryKey, oldData => {
          return updateQueryData(oldData, {
            slug: feature.slug,
            enrolled: !feature.enrolled,
          })
        })

        const response = await responsePromise
        if (!response.ok) {
          throw new Error(`Failed to toggle feature preview: ${response.statusText}`)
        }

        onShowBanner('reload')

        // eslint-disable-next-line unused-imports/no-unused-vars
      } catch (error) {
        onShowBanner('error')
      }
    },
    [queryClient, queryAPI],
  )

  return {
    ...featureQuery,
    toggleEnrollment,
  }
}
